import { crmRequest } from '../http'
import type {
  CrmMember,
  CrmMemberInput,
  CrmMemberList,
  CrmMemberPatch,
  CrmOrganisation,
  CrmOrganisationInput,
  CrmOrganisationListOut,
  CrmOrganisationMatch,
  OrganisationSort,
} from './types'

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '' && value !== false) search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export function listOrganisations(params: {
  q?: string
  page?: number
  page_size?: number
  sort?: OrganisationSort
  include_archived?: boolean
}): Promise<CrmOrganisationListOut> {
  return crmRequest(`/organisations${query(params)}`)
}

/** A merged organisation answers `{moved_to}` (the id it was merged into) instead of its detail. */
export function getOrganisation(id: string): Promise<CrmOrganisation | { moved_to: string }> {
  return crmRequest(`/organisations/${id}`)
}

export function createOrganisation(input: CrmOrganisationInput): Promise<CrmOrganisation> {
  return crmRequest('/organisations', { method: 'POST', body: JSON.stringify(input) })
}

export function updateOrganisation(
  id: string,
  patch: CrmOrganisationInput,
): Promise<CrmOrganisation> {
  return crmRequest(`/organisations/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

export function deleteOrganisation(id: string): Promise<void> {
  return crmRequest(`/organisations/${id}`, { method: 'DELETE' })
}

export function restoreOrganisation(id: string): Promise<CrmOrganisation> {
  return crmRequest(`/organisations/${id}/restore`, { method: 'POST' })
}

export function setOrganisationLabels(id: string, labelIds: string[]): Promise<CrmOrganisation> {
  return crmRequest(`/organisations/${id}/labels`, {
    method: 'PUT',
    body: JSON.stringify({ label_ids: labelIds }),
  })
}

export function matchOrganisations(params: {
  name?: string
  website?: string
  email?: string
  kvk?: string
  exclude_id?: string
}): Promise<CrmOrganisationMatch[]> {
  return crmRequest(`/organisations/match${query(params)}`)
}

export function getOrganisationByLegacyPerson(personId: string): Promise<{ id: string }> {
  return crmRequest(`/organisations/by-legacy-person/${personId}`)
}

export function listMembers(
  organisationId: string,
  params: { include_former?: boolean; limit?: number } = {},
): Promise<CrmMemberList> {
  return crmRequest(`/organisations/${organisationId}/members${query(params)}`)
}

export function addMember(organisationId: string, input: CrmMemberInput): Promise<CrmMember> {
  return crmRequest(`/organisations/${organisationId}/members`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateMember(memberId: string, patch: CrmMemberPatch): Promise<CrmMember> {
  return crmRequest(`/organisation-members/${memberId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function removeMember(memberId: string): Promise<void> {
  return crmRequest(`/organisation-members/${memberId}`, { method: 'DELETE' })
}

export function restoreMember(memberId: string): Promise<CrmMember> {
  return crmRequest(`/organisation-members/${memberId}/restore`, { method: 'POST' })
}
